import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import type { FieldDef, FieldGroup } from '../data/fieldCatalog';
type CustomizeFieldsMenuProps<T> = {
  fields: FieldDef<T>[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onReset: () => void;
  /** Column that can never be hidden, e.g. the borrower name. */
  anchorLabel: string;
};
export function CustomizeFieldsMenu<T>({
  fields,
  selectedIds,
  onToggle,
  onMove,
  onReset,
  anchorLabel
}: CustomizeFieldsMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const selected = useMemo(() => selectedIds.map(id => fields.find(field => field.id === id)).filter((field): field is FieldDef<T> => Boolean(field)), [fields, selectedIds]);
  const grouped = useMemo(() => {
    const term = query.trim().toLowerCase();
    const matches = term ? fields.filter(field => field.label.toLowerCase().includes(term)) : fields;
    return matches.reduce<Record<string, FieldDef<T>[]>>((acc, field) => {
      acc[field.group] = [...(acc[field.group] ?? []), field];
      return acc;
    }, {});
  }, [fields, query]);
  const groups = Object.keys(grouped) as FieldGroup[];
  return <div className="relative" ref={containerRef} data-id="element-105" data-mp="23oxl5hst12h7">
      <button type="button" onClick={() => setOpen(prev => !prev)} aria-expanded={open} aria-haspopup="true" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12.5px] font-medium text-ink transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand/30" data-id="element-106" data-mp="28hieki7stdk7">
        <SlidersHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-107" data-mp="2rfuq2am6mziy" />
        Fields
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600" data-id="element-108" data-mp="2dg20wjqb0rd3">
          {selected.length}
        </span>
      </button>

      <AnimatePresence data-id="element-109" data-mp="2idld7e3fyu2e">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -6,
        scale: 0.98
      }} transition={{
        duration: 0.14
      }} className="absolute right-0 z-40 mt-2 w-[340px] origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl" data-id="element-110" data-mp="120wmwjkq36e6">
            <div className="border-b border-gray-200 px-4 py-3" data-id="element-111" data-mp="ujqcw0z9ed6i">
              <div className="flex items-center gap-2" data-id="element-112" data-mp="18gn0zunztgjp">
                <h2 className="text-[13.5px] font-semibold text-ink" data-id="element-113" data-mp="13hk6331q8qpg">Customize fields</h2>
                <button type="button" onClick={onReset} className="ml-auto text-[12px] font-medium text-brand hover:text-brand-dark" data-id="element-114" data-mp="fs7ww36egin5">
                  Reset
                </button>
              </div>
              <p className="mt-0.5 text-[11.5px] text-gray-500" data-id="element-115" data-mp="pkdkz1tj6fx2">
                {anchorLabel} is always shown. Order here is the order in your view.
              </p>
            </div>

            {selected.length > 0 && <ul className="space-y-1 border-b border-gray-200 px-3 py-2.5" data-id="element-116" data-mp="3o8kj90d3ws1v">
                {selected.map((field, index) => <li key={field.id} className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2 py-1.5" data-id="element-117" data-mp="2rhz1omn1ej04" data-mpi={index}>
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink" data-id="element-118" data-mp="30huboo4uw7yk">
                      {index + 1}. {field.label}
                    </span>
                    <button type="button" onClick={() => onMove(field.id, -1)} disabled={index === 0} aria-label={`Move ${field.label} left`} className="rounded p-1 text-gray-500 transition-colors hover:bg-white disabled:opacity-30" data-id="element-119" data-mp="35af5hpnril23">
                      <ArrowLeftIcon className="h-3 w-3" aria-hidden="true" data-id="element-120" data-mp="3o656b9jylbee" />
                    </button>
                    <button type="button" onClick={() => onMove(field.id, 1)} disabled={index === selected.length - 1} aria-label={`Move ${field.label} right`} className="rounded p-1 text-gray-500 transition-colors hover:bg-white disabled:opacity-30" data-id="element-121" data-mp="1psvgtsoo16mz">
                      <ArrowRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-122" data-mp="1ckuzdrgyvr1g" />
                    </button>
                    <button type="button" onClick={() => onToggle(field.id)} aria-label={`Remove ${field.label}`} className="rounded p-1 text-gray-500 transition-colors hover:bg-white hover:text-danger" data-id="element-123" data-mp="18j9ilv1baslz">
                      <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-124" data-mp="1w697wc7bmg8l" />
                    </button>
                  </li>)}
              </ul>}

            <div className="border-b border-gray-200 px-3 py-2.5" data-id="element-125" data-mp="ksx9igubyptt">
              <div className="relative" data-id="element-126" data-mp="ftke37pe8afm">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-127" data-mp="fsiyqr1ch1s3" />
                <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search fields" aria-label="Search fields" className="w-full rounded-lg border border-gray-200 py-1.5 pl-8 pr-2.5 text-[12.5px] text-ink placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" data-id="element-128" data-mp="m8ht83zy4zdk" />
              </div>
            </div>

            <div className="scrollbar-thin max-h-[280px] overflow-y-auto px-3 py-2" data-id="element-129" data-mp="3kt17btzyw1gb">
              {groups.map(group => <div key={group} className="mb-2 last:mb-0" data-id="element-130" data-mp="3po6s2phmva2t">
                  <p className="px-1 pb-1 text-[10.5px] font-semibold uppercase tracking-widest text-gray-400" data-id="element-131" data-mp="3bs2zt1qblbsz">
                    {group}
                  </p>
                  <ul data-id="element-132" data-mp="2x3t6xdlhplqb">
                    {grouped[group].map(field => {
                const isSelected = selectedIds.includes(field.id);
                return <li key={field.id} data-id="element-133" data-mp="364reg5v50bdn">
                          <button type="button" onClick={() => onToggle(field.id)} aria-pressed={isSelected} className="flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left transition-colors hover:bg-gray-50" data-id="element-134" data-mp="365su6nn3lm1p">
                            <span aria-hidden="true" className={['flex h-4 w-4 shrink-0 items-center justify-center rounded border', isSelected ? 'border-brand bg-brand text-white' : 'border-gray-300 bg-white'].join(' ')} data-id="element-135" data-mp="3psgy1psx75bs">
                              {isSelected && <CheckIcon className="h-3 w-3" data-id="element-136" data-mp="1a0gj920rqfh3" />}
                            </span>
                            <span className="text-[12.5px] text-ink" data-id="element-137" data-mp="1e21yuv4ot7uz">{field.label}</span>
                          </button>
                        </li>;
              })}
                  </ul>
                </div>)}
              {groups.length === 0 && <p className="px-1 py-6 text-center text-[12px] text-gray-500" data-id="element-138" data-mp="qch5rjetku7z">
                  No fields match “{query}”.
                </p>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}